import { Injectable } from '@angular/core';
import { ItemData } from '../models/pickup/item-data.model';
import { PartyData } from '../models/pickup/party-data.model';
import { PickupData } from '../models/pickup/pickup-data.model';
import { ServicesData } from '../models/pickup/services-data.model';
import { StoreService } from './store.service';

@Injectable({
  providedIn: 'root',
})
export class PickupService {
  constructor(private storeService: StoreService) {}

  public buildPickupData(): PickupData {
    const preAuthData = this.storeService.getPreAuthData();
    const shippingCourierData = this.storeService.getShippingCourierFormData();
    const shippingPointData = this.storeService.getShippingPointFormData();
    const shipmentFormData = this.storeService.getShipmentFormData();

    const pickupData: PickupData = {
      dropOffAtPoint: shippingCourierData === undefined,
      shipNo: preAuthData?.shipNo,
      pckSenderEmail: shippingCourierData?.email ?? shippingPointData?.email,
      pckSenderPhone: shippingCourierData?.phone ?? shippingPointData?.phone,
      pckDate: shippingCourierData?.date,
      pckInfo: shippingCourierData?.instructions,
      emailForLabel:
        shippingCourierData?.emailForLabel ?? shippingPointData?.emailForLabel,
    };

    if (!pickupData.shipNo) {
      pickupData.ship = {
        clientExt1Id: preAuthData?.payerNo?.toString(),
        createDate: this.getCurrentFormattedDate(),
        modifyDate: this.getCurrentFormattedDate(),
        plannedDate: this.getCurrentFormattedDate(),
        notes: shippingCourierData?.instructions,
        orderType: 'SHIPMENT',
        paymentType: 2,
        product: this.storeService.getDeliveryCourierFormData() ? 'ND' : 'DTR',
        paymentKind: 4,
        parties: this.buildParties(),
        items: this.buildItems(),
        services: this.buildServices(),
      };
    }

    return pickupData;
  }

  private buildParties(): PartyData[] {
    const shippingCourierData = this.storeService.getShippingCourierFormData();
    const deliveryCourierData = this.storeService.getDeliveryCourierFormData();
    const shippingPointData = this.storeService.getShippingPointFormData();
    const deliveryPointData = this.storeService.getDeliveryPointFormData();

    const senderParty = shippingCourierData
      ? this.createPartyData(shippingCourierData, 1)
      : this.createPartyData(shippingPointData, 1);

    const receiverParty = deliveryCourierData
      ? this.createPartyData(deliveryCourierData, 2)
      : this.createPartyData(deliveryPointData, 2);

    const dtrParty = deliveryPointData
      ? {
          orpType: 3,
          ext1Id: deliveryPointData.pointPartnerCode,
          ext2Id: deliveryPointData.pointId,
        }
      : undefined;

    return [receiverParty, senderParty, dtrParty].filter(
      Boolean
    ) as PartyData[];
  }

  private buildItems(): ItemData[] {
    const shipmentFormData = this.storeService.getShipmentFormData();

    if (shipmentFormData?.parcels) {
      return shipmentFormData.parcels.map(this.createItemData('PARCEL'));
    }

    return (shipmentFormData?.palettes ?? []).map(
      this.createItemData('PALETTE')
    );
  }

  private buildServices(): ServicesData {
    const shipmentFormData = this.storeService.getShipmentFormData();

    return {
      COD: [
        {
          serviceDliCode: 'COD',
          value: shipmentFormData?.isCod ? '1' : '0',
        },
        {
          serviceDliCode: 'COD_VALUE',
          value: shipmentFormData?.codAmount?.toString(),
        },
        {
          serviceDliCode: 'COD_ACCOUNT',
          value: shipmentFormData?.accountNumber?.toString(),
        },
      ],
      INS: [
        {
          serviceDliCode: 'INS',
          value: shipmentFormData?.isIns ? '1' : '0',
        },
        {
          serviceDliCode: 'INS_VALUE',
          value: shipmentFormData?.insAmount?.toString(),
        },
        {
          serviceDliCode: 'INS_PCK_CONTENT',
          value: shipmentFormData?.insShipmentContentDescription?.toString(),
        },
      ],
    };
  }

  private getCurrentFormattedDate(): string {
    const date = new Date();
    const [year, month, day] = [
      date.getFullYear(),
      this.padToTwoDigits(date.getMonth() + 1),
      this.padToTwoDigits(date.getDate()),
    ];
    const [hours, minutes, seconds] = [
      this.padToTwoDigits(date.getHours()),
      this.padToTwoDigits(date.getMinutes()),
      this.padToTwoDigits(date.getSeconds()),
    ];

    const offset = -date.getTimezoneOffset();
    const sign = offset >= 0 ? '+' : '-';
    const hoursOffset = this.padToTwoDigits(Math.floor(Math.abs(offset) / 60));
    const minutesOffset = this.padToTwoDigits(Math.abs(offset) % 60);

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${hoursOffset}:${minutesOffset}`;
  }

  private createPartyData(data: any, orpType: number): PartyData {
    return {
      address1: this.buildAddress(
        data?.street,
        data?.houseNumber,
        data?.apartmentNumber
      ),
      city: data?.city,
      companyName: data?.company,
      email: data?.email,
      name: data?.name,
      orpType,
      phone: data?.phone,
      postalCode: data?.zipCode,
      act: 1,
    };
  }

  private createItemData(type: string) {
    return (data: any): ItemData => ({
      compWeight: data?.weight,
      height: data?.height,
      length: data?.length,
      oriTypeDliCode: type,
      quantity: data?.count,
      weight: data?.weight,
      width: data?.width,
      irregularShape: data?.isNotStandard ? 1 : 0,
      act: 1,
    });
  }

  private buildAddress(
    street?: string,
    houseNumber?: string,
    apartmentNumber?: string
  ): string {
    const baseAddress = `${street ?? ''} ${houseNumber ?? ''}`.trim();
    return apartmentNumber ? `${baseAddress}/${apartmentNumber}` : baseAddress;
  }

  private padToTwoDigits(num: number): string {
    return num.toString().padStart(2, '0');
  }
}
